/**
 * A governed act's input, as fields the shared Form can render (#1131).
 *
 * ── why this is a pure function in its own file ────────────────────────────
 * Ruling 40821 point 3 admits a generated form only for a "bounded, simple
 * input schema", and `core.tool_schema_is_form_safe` is what decides that — in
 * SQL, at registration, so an unrenderable schema is refused before anything
 * reaches a screen. This is the client half of the same contract, and it must
 * agree with it exactly. A mapping buried inside a component is one nobody can
 * put a schema into and read the answer out of, which is how the two halves
 * drift.
 *
 * ── the identity argument is REMOVED, not disabled ─────────────────────────
 * The ruling says the form renders "with record identity prefilled and
 * immutable". A disabled field showing a uuid is neither: it is furniture that
 * invites a question, and on a batch action there is no single id to show.
 * The surface supplies the identity when it invokes; the person never sees it.
 *
 * ── a scalar the person CHOOSES rather than types (MB-TAGVU#23, ROOT c.119149)
 * An opaque reference — a crew member, a reviewer — is a plain string to the
 * verb and a NAME to the person. Rendered as text it asks someone to type an
 * id they cannot know. One presentation annotation on that string says where
 * its choices come from:
 *
 *   "x-solum-lookup": { "tool": "<module>.<name>",
 *                       "args": { "<sourceArg>": { "from": "<action argument>" | "$search" } } }
 *
 * and nothing else. `tool` is resolved ONLY in the caller's own describeTools
 * manifest, by exact equality of BOTH `module` and `name`; the source must be a
 * non-mutating read the caller may run without a confirmation; it is invoked
 * through its own `specToTool` handler, never through a bind, function or url
 * the annotation could carry. `from` names a declared argument of THIS action
 * (record identity included — the binding supplies it) or the person's typed
 * text. No expressions, paths, constants or response mapping: the source
 * answers `{items:[{id,primary,secondary?}],complete}` — under its OWN bind's
 * `returns.as` where it declares one (`unwrapSourceAnswer`) — or it is not a
 * source.
 *
 * Anything that does not resolve makes the field UNAVAILABLE, with the reason.
 * It never falls back to a text box: a box asking for a raw id is exactly the
 * defect this exists to remove, and silently restoring it on a typo would hide
 * the typo behind the defect.
 *
 * `core.tool_schema_is_form_safe` reads only `type` and `enum` per property, so
 * the annotated string stays a form-safe scalar and needs no SQL change.
 */
import type { McpToolSpec } from '@solum/sdk'

/** The one presentation annotation a scalar string input may carry. */
export const LOOKUP_ANNOTATION = 'x-solum-lookup'
/** The reserved `from`: the text the person typed into the chooser. */
export const LOOKUP_SEARCH = '$search'

/** The subset of JSON Schema a form-safe tool input can use. */
interface PropSpec {
  type?: string
  title?: string
  description?: string
  enum?: unknown[]
  [LOOKUP_ANNOTATION]?: unknown
}

/** A choice source, resolved in the caller's manifest and admitted as a read. */
export interface ActionLookup {
  /** The source tool, exactly as the caller's describeTools() returned it. */
  source: McpToolSpec
  /** source argument → the action argument it is read from, or `$search`. */
  args: Record<string, string>
  /** True when the source is told the typed text; else only a complete answer is filtered here. */
  searches: boolean
}

export interface ActionFormField {
  name: string
  type: 'text' | 'number' | 'switch' | 'select' | 'relation'
  label: string
  required: boolean
  options?: { value: string; label: string }[]
  hint?: string
  /** `relation` only: where the choices come from. */
  lookup?: ActionLookup
  /** `relation` only: why no choice can be offered. Set exactly when `lookup` is not. */
  unavailable?: string
}

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v)

/** `<module>.<name>` — exactly one dot, neither half empty. Tool names carry no dot. */
const TOOL_REF = /^([^.\s]+)\.([^.\s]+)$/

/**
 * Resolve one `x-solum-lookup` annotation, or say why it cannot be offered.
 *
 * `declared` is every argument the ACTION declares, identity included.
 * `tools` is the caller's describeTools() answer; `undefined` while it is not
 * in hand, which is a different sentence from "not in it".
 */
export function resolveActionLookup(
  annotation: unknown,
  declared: ReadonlySet<string>,
  tools: readonly McpToolSpec[] | undefined,
): { lookup: ActionLookup } | { unavailable: string } {
  const malformed = (why: string) => ({ unavailable: `This choice is misconfigured: ${why}` })
  /* Words, never keys (marble non-negotiable 6): the reason names WHAT is
     wrong, and the annotation itself is where an author finds WHICH. */
  if (!isPlainObject(annotation)) return malformed('its source is not described.')
  if (Object.keys(annotation).some((k) => k !== 'tool' && k !== 'args')) {
    return malformed('it carries something other than a source and its arguments.')
  }
  const ref = typeof annotation.tool === 'string' ? TOOL_REF.exec(annotation.tool) : null
  if (!ref) return malformed('its source is not named as one module and one tool.')
  const [, sourceModule, sourceName] = ref as unknown as [string, string, string]
  const rawArgs = annotation.args ?? {}
  if (!isPlainObject(rawArgs)) return malformed('its arguments are not described.')

  const args: Record<string, string> = {}
  for (const [sourceArg, spec] of Object.entries(rawArgs)) {
    /* `from` and nothing else: a `const`, a `default` or a path here would be
       the start of an expression language, and ignoring one would leave the
       author believing it applied. */
    if (!isPlainObject(spec) || Object.keys(spec).length !== 1 || typeof spec.from !== 'string') {
      return malformed('an argument is not read from exactly one place.')
    }
    if (spec.from !== LOOKUP_SEARCH && !declared.has(spec.from)) {
      return malformed('an argument is read from something this action does not declare.')
    }
    args[sourceArg] = spec.from
  }

  if (!tools) return { unavailable: 'The tools you may use have not loaded, so this choice cannot be offered yet.' }
  const source = tools.find((tool) => tool.module === sourceModule && tool.name === sourceName)
  if (!source) return { unavailable: 'Its source is not among the tools you may use, so this choice cannot be offered.' }
  if (source.mutates !== false) {
    return { unavailable: 'Its source changes data, so it cannot supply choices.' }
  }
  /* `policyEffect` absent is the declared default: describeTools() carries no
     policy layer, and a read's default is allow. Present and not `allow`, the
     source would stop for a person's confirmation — that is not a lookup. */
  if (source.requiresConfirmation === true || source.requiresApproval === true
    || (source.policyEffect !== undefined && source.policyEffect !== 'allow')) {
    return { unavailable: 'Its source needs a confirmation before it runs, so it cannot supply choices.' }
  }
  const accepts = isPlainObject(source.inputSchema?.properties) ? source.inputSchema.properties : {}
  /* An argument the source does not declare is dropped by its bind without a
     word — the text would never reach it and an unsearched answer would be
     shown as the answer to the text. */
  if (Object.keys(args).some((sourceArg) => !Object.prototype.hasOwnProperty.call(accepts, sourceArg))) {
    return malformed('its source does not take one of the arguments it is given.')
  }
  return { lookup: { source, args, searches: Object.values(args).includes(LOOKUP_SEARCH) } }
}

/** The source's arguments: named action arguments, and the typed text for `$search`. */
export function lookupSourceArgs(
  lookup: ActionLookup, known: Record<string, unknown>, query: string,
): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [sourceArg, from] of Object.entries(lookup.args)) {
    const value = from === LOOKUP_SEARCH ? query.trim() : known[from]
    if (value !== undefined) out[sourceArg] = value
  }
  return out
}

/** One choice, as the standard answer carries it. */
export interface LookupChoice { id: string; primary: string; secondary?: string }

/** The answer did not have the standard shape. Distinct from an empty answer. */
export class LookupAnswerError extends Error {
  constructor() {
    super('Its source answered in a shape this chooser cannot read, so nothing is offered.')
    this.name = 'LookupAnswerError'
  }
}

/** A source with no search arm returned part of its set, and text was typed. */
export class LookupIncompleteError extends Error {
  constructor() {
    super('This list came back incomplete and cannot be searched here. Clear the search to choose from what it returned.')
    this.name = 'LookupIncompleteError'
  }
}

const isLabel = (v: unknown) => typeof v === 'string' && v.trim() !== ''

/**
 * Undo exactly what the source's OWN bind did to its answer (ROOT c.119372).
 *
 * `specToTool` passes an rpc or entity bind's result through the SDK's
 * `shapeReturn`: no `returns` → the read's value; `returns.literal` → the
 * literal, the read's value discarded; `returns.as` → `{ [as]: value }`. A
 * builtin bind is not shaped. This is that function read backwards, from the
 * RESOLVED source's bind and nothing else — the annotation still names no
 * response path, and MCP keeps the same wrapper. The wrapper must be exactly
 * the one key `shapeReturn` writes; anything else is malformed, never a second
 * guess at the bare shape.
 */
export function unwrapSourceAnswer(source: McpToolSpec, value: unknown): unknown {
  const bind = isPlainObject(source.bind) ? source.bind : {}
  if (bind.kind === 'builtin') return value
  const returns = isPlainObject(bind.returns) ? bind.returns : undefined
  if (!returns) return value
  /* A literal REPLACES the read's value: nothing the source looked up is left. */
  if (returns.literal !== undefined) throw new LookupAnswerError()
  if (!returns.as) return value
  const key = String(returns.as)
  if (!isPlainObject(value) || Object.keys(value).length !== 1
    || !Object.prototype.hasOwnProperty.call(value, key)) throw new LookupAnswerError()
  return value[key]
}

/**
 * Read `{items:[{id,primary,secondary?}],complete}` — plain strings only.
 *
 * Other top-level keys are tolerated (an existing read may keep its own
 * fields beside the projection); nothing is renamed or mapped.
 */
export function readLookupAnswer(value: unknown): { items: LookupChoice[]; complete: boolean } {
  if (!isPlainObject(value) || !Array.isArray(value.items) || typeof value.complete !== 'boolean') {
    throw new LookupAnswerError()
  }
  const items = value.items.map((item): LookupChoice => {
    if (!isPlainObject(item) || !isLabel(item.id) || !isLabel(item.primary)
      || !(item.secondary == null || typeof item.secondary === 'string')) throw new LookupAnswerError()
    return {
      id: item.id as string,
      primary: item.primary as string,
      ...(typeof item.secondary === 'string' && item.secondary.trim() ? { secondary: item.secondary } : {}),
    }
  })
  return { items, complete: value.complete }
}

/**
 * What the chooser shows for one question.
 *
 * A searched source already answered the text. An unsearched one is filtered
 * HERE, and only when it said its set is complete: filtering a truncated set
 * would present "no match" for a person who simply was not in the first page.
 */
export function lookupChoices(
  answer: { items: LookupChoice[]; complete: boolean }, query: string, searches: boolean,
): { items: LookupChoice[]; complete: boolean } {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean)
  if (searches || words.length === 0) return answer
  if (!answer.complete) throw new LookupIncompleteError()
  return {
    items: answer.items.filter((item) => {
      const hay = `${item.primary} ${item.secondary ?? ''}`.toLowerCase()
      return words.every((word) => hay.includes(word))
    }),
    complete: true,
  }
}

/** `p_cost_centre` → `Cost centre`. Only used when the schema names nothing better. */
export function humanise(name: string): string {
  const words = name.replace(/^p_/, '').replace(/[_-]+/g, ' ').trim()
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : name
}

/**
 * The fields a person must fill to take this act.
 *
 * `exclude` carries the identity arguments. Returns `[]` for an act that needs
 * nothing beyond the record — which is the common case and the reason the
 * caller can tell "no form needed" from "a form with no fields".
 *
 * `tools` is the caller's describeTools() answer, read only to resolve an
 * `x-solum-lookup`; a field carrying one is a `relation` either way, so the
 * count the launcher reads does not depend on whether the manifest has loaded.
 */
export function actionFormFields(
  schema: Record<string, unknown> | null | undefined,
  exclude: (string | null)[] = [],
  tools?: readonly McpToolSpec[],
): ActionFormField[] {
  const props = (schema?.properties ?? {}) as Record<string, PropSpec>
  const required = new Set(Array.isArray(schema?.required) ? (schema.required as string[]) : [])
  const skip = new Set(exclude.filter((x): x is string => !!x))
  const declared = new Set(Object.keys(props))

  return Object.entries(props)
    .filter(([name]) => !skip.has(name))
    .map(([name, spec]) => {
      const label = spec.title?.trim() || humanise(name)
      const hint = spec.description?.trim() || undefined
      if (spec[LOOKUP_ANNOTATION] !== undefined) {
        const base = { name, type: 'relation' as const, label, required: required.has(name), hint }
        if (spec.type !== 'string' || spec.enum !== undefined) {
          return { ...base, unavailable: 'This choice is misconfigured: a choice source is admitted only on a plain text argument.' }
        }
        return { ...base, ...resolveActionLookup(spec[LOOKUP_ANNOTATION], declared, tools) }
      }
      if (Array.isArray(spec.enum) && spec.enum.length > 0) {
        return {
          name,
          type: 'select' as const,
          label,
          required: required.has(name),
          hint,
          options: spec.enum.map((v) => ({ value: String(v), label: humanise(String(v)) })),
        }
      }
      /* The registrar admits string | number | integer | boolean and nothing
         else, so an unknown type here means the two halves have drifted. Render
         it as text rather than dropping it: a field the person cannot see is a
         value the verb will refuse for, with nothing on screen to explain it. */
      const type = spec.type === 'boolean' ? 'switch'
        : spec.type === 'number' || spec.type === 'integer' ? 'number'
          : 'text'
      return { name, type, label, required: required.has(name), hint }
    })
}
